import emailjs from '@emailjs/browser';
import { supabase } from './supabase';

export async function sendDocumentByEmail(documentId: string, clientEmail: string, subject: string, message: string) {
  try {
    // Fetch EmailJS configuration - limit to first record
    const { data: config, error: configError } = await supabase
      .from('emailjs_config')
      .select('*')
      .limit(1)
      .single();

    if (configError) {
      console.error('Error fetching EmailJS config:', configError);
      throw new Error('Failed to load email configuration');
    }

    if (!config) {
      console.error('No EmailJS configuration found');
      throw new Error('EmailJS configuration not found');
    }

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Failed to get current user');
    }

    // Fetch user profile to get company name
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company, name')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching profile:', profileError);
      throw new Error('Failed to load company information');
    }

    // Fetch document information
    const { data: document, error: documentError } = await supabase
      .from('documents')
      .select('type, number')
      .eq('id', documentId)
      .single();

    if (documentError) {
      console.error('Error fetching document:', documentError);
      throw new Error('Failed to load document information');
    }

    // Generate PDF URL
    const pdfUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf?id=${documentId}`;

    // Fetch the PDF content
    const response = await fetch(pdfUrl, {
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    // Convert PDF to base64
    const pdfBlob = await response.blob();
    const reader = new FileReader();
    const base64PDF = await new Promise<string>((resolve) => {
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]); // Remove data URL prefix
      };
      reader.readAsDataURL(pdfBlob);
    });

    const documentTypeNumber = `${document.type === 'invoice' ? 'Facture' : 'Devis'} #${document.number}`;

    const templateParams = {
      to_email: clientEmail,
      subject: subject,
      message: message,
      document_id: documentId,
      sender_company: profile.company || profile.name || 'Your Company',
      sender_email: user.email,
      document_type_number: documentTypeNumber,
      attachment: base64PDF,
      attachment_name: `${document.type === 'invoice' ? 'Facture' : 'Devis'}_${document.number}.pdf`,
    };

    const emailResponse = await emailjs.send(
      config.service_id,
      config.template_id,
      templateParams,
      config.public_key
    );

    if (emailResponse.status !== 200) {
      throw new Error(`Failed to send email (Status: ${emailResponse.status})`);
    }

    console.log('Email sent successfully:', emailResponse.status);
    return { success: true, message: 'Email sent successfully' };
  } catch (error) {
    console.error('Error in sendDocumentByEmail:', error);
    throw error instanceof Error 
      ? error 
      : new Error('An unexpected error occurred while sending the email');
  }
}